// Oireachtas schedule maker (card 524449bc, plan Phase 1): the board's state
// from GET /oireachtas/:id/schedule and back, block headings, labels and the
// CSV export. Pure functions. The admin app carries a verbatim copy: keep the
// two in step.
//
// The unit is a competition-round ("card"): SC-11-G round 1, round 2 and
// round 3 are three cards placed apart. One schedule document holds every day;
// a save sends all of it with the revision it was loaded at (409 when someone
// else saved since).
import { flow, formatTime, parseTime } from '../../ManageFeisGrade/RedirectFeis/Schedule/scheduleTiming';
import { compareComps } from '../../../Teacher/Oireachtas/compSort';
import { cardMinutes } from './oireachtasTiming';

export const DEFAULT_START = 8 * 60; // a new stage starts at 8:00 am
export const DEFAULT_NOTE = 15; // minutes
export const NOTE_MIN = 1; // the backend's bound (code review R1-F3)
export const NOTE_MAX = 600; // the backend's bound (code review R1-F3)
export const ROUND_MAX = 600;

export const cardKey = (compId, round) => `${compId}:${round}`;
export const itemKeyOf = (compId, round) => `r-${compId}-${round}`;

let seq = 0;
export const newId = (prefix) => {
  seq += 1;
  const rnd = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${seq}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`;
};

// "2026-11-06" -> "Fri Nov 6" (a calendar date: no time zone involved)
export const dayLabel = (day, long = false) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return String(day || '');
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString('en-CA', { timeZone: 'UTC', weekday: long ? 'long' : 'short', month: long ? 'long' : 'short', day: 'numeric' });
};

// Dancers a card counts: the locked draw once there is one, else paid entries.
export const entrantsOf = (comp) => {
  const locked = Number(comp?.lockedCount) || 0;
  return locked > 0 ? locked : Number(comp?.entrants) || 0;
};

// ---- load -------------------------------------------------------------------

// competitions: [{ _id, code, name, group, entrants, lockedCount, cards: [spec] }]
// schedule: { revision, days: [{ day, stages: [{ stage, stageName, startTime, items }] }] }
// days: the event's days ("YYYY-MM-DD"). A saved day the event no longer has
// is left out and its cards go back to To schedule (`dropped` counts them), so
// the next save holds only the event's days.
export const buildBoard = (competitions, schedule, days) => {
  const comps = {};
  const cards = {};
  const order = [];
  // syllabus order (the panel's own sort), so To schedule reads like the syllabus
  for (const c of [...(competitions || [])].sort(compareComps)) {
    const id = String(c._id);
    comps[id] = c;
    for (const spec of c.cards || []) {
      const key = cardKey(id, spec.round);
      cards[key] = { key, compId: id, ...spec };
      order.push(key);
    }
  }
  const placed = new Set();
  const saved = new Map((schedule?.days || []).map((d) => [String(d.day), d]));
  const dayList = [...(days || [])];
  let dropped = 0;
  for (const [d, savedDay] of saved) {
    if (dayList.includes(d)) continue;
    for (const s of savedDay.stages || []) dropped += (s.items || []).filter((it) => it.type === 'round').length;
  }
  const board = {};
  for (const day of dayList) {
    const savedDay = saved.get(day);
    board[day] = [...(savedDay?.stages || [])]
      .sort((a, b) => (Number(a.stage) || 0) - (Number(b.stage) || 0))
      .map((s, i) => ({
        id: newId('stage'),
        name: String(s.stageName || '').trim() || `Stage ${i + 1}`,
        startMin: parseTime(s.startTime) ?? DEFAULT_START,
        items: (s.items || []).map((it) => {
          const fixedAt = parseTime(it.fixedAt);
          if (it.type === 'note') {
            return {
              key: String(it.key || newId('note')),
              type: 'note',
              label: String(it.label || '').trim() || 'Note',
              // a stored length within the backend's 1-600 is kept as is (code review R1-F3)
              minutes: Math.min(NOTE_MAX, Math.max(1, Math.round(Number(it.minutes)) || DEFAULT_NOTE)),
              fixed: fixedAt != null,
              fixedAt,
            };
          }
          const compId = String(it.competition_id || '');
          const round = Number(it.round) || 1;
          const key = cardKey(compId, round);
          const ok = Boolean(cards[key]) && !placed.has(key);
          if (ok) placed.add(key);
          return {
            // a flagged duplicate gets its own key so the board's keys stay unique
            key: ok ? itemKeyOf(compId, round) : newId('bad'),
            type: 'round',
            competitionId: compId,
            round,
            code: comps[compId]?.code || it.code || '',
            atATime: Number(it.atATime) || null,
            fixed: fixedAt != null,
            fixedAt,
            problem: ok ? null : cards[key] ? 'duplicate' : 'missing',
          };
        }),
      }));
  }
  const pool = order.filter((k) => !placed.has(k));
  return { comps, cards, order, days: dayList, board, pool, dropped, revision: Number(schedule?.revision) || 0 };
};

// ---- labels -----------------------------------------------------------------

export const cardOf = (item, cards) => (item?.type === 'round' ? cards[cardKey(item.competitionId, item.round)] || null : null);

// "38 dancers", "7 teams", "≈19 recalled"
export const countLine = (card, comp) => {
  const n = entrantsOf(comp);
  if (card?.kind === 'team') return `${n} team${n === 1 ? '' : 's'}`;
  if (card?.recall) {
    if (card.recallAll || n <= 20) return `all ${n} recalled`;
    return `≈${Math.ceil(n / 2)} recalled of ${n}`;
  }
  return `${n} dancer${n === 1 ? '' : 's'}`;
};

// The big count on a card (Devin 2026-10-07): the number, with a word only
// when it isn't dancers (teams, the expected recall).
// Round 3 shows the recall it expects (half above 20, all at 20 or fewer).
export const countFigure = (card, comp) => {
  const n = entrantsOf(comp);
  if (card?.kind === 'team') return { value: String(n), label: n === 1 ? 'team' : 'teams' };
  if (card?.recall) {
    if (card.recallAll || n <= 20) return { value: String(n), label: 'recalled' };
    return { value: `≈${Math.ceil(n / 2)}`, label: `recalled of ${n}` };
  }
  return { value: String(n), label: '' }; // just the number (Devin 2026-10-07: no "dancers")
};

export const atATimeLine = (card, k) => {
  if (card?.kind === 'reel_continuous') return 'one after another';
  if (card?.kind === 'team') return `${k} team${k === 1 ? '' : 's'} at a time`;
  return `${k} at a time`;
};

// Block headings: a heading starts each run of consecutive cards with the same
// heading (ECRO's "Solo Champ Hard Shoe" blocks). Notes break a run.
export const headingsOf = (items, cards) => {
  let prev = null;
  return (items || []).map((it) => {
    const card = cardOf(it, cards);
    if (!card || it.problem) {
      prev = null;
      return null;
    }
    const h = card.heading || null;
    const show = h && h !== prev ? h : null;
    prev = h;
    return show;
  });
};

// ---- pool -------------------------------------------------------------------

// Girls / Boys / Mixed pills on To schedule (Devin 2026-10-07). Ladies count
// as girls and men as boys (the 18+ values of the same ladders).
export const SEXES = [
  { key: 'girls', label: 'Girls' },
  { key: 'boys', label: 'Boys' },
  { key: 'mixed', label: 'Mixed' },
];
export const sexOf = (comp) => {
  const g = String(comp?.gender || '').toLowerCase();
  if (g === 'girls' || g === 'ladies') return 'girls';
  if (g === 'boys' || g === 'men') return 'boys';
  return 'mixed';
};
// pills for the sexes the event has, each with how many of its rounds are left
export const sexChips = (order, pool, cards, comps) => {
  const has = new Set(order.map((k) => sexOf(comps[cards[k]?.compId])));
  const left = {};
  for (const k of pool || []) {
    const sx = sexOf(comps[cards[k]?.compId]);
    left[sx] = (left[sx] || 0) + 1;
  }
  return SEXES.filter((x) => has.has(x.key)).map((x) => ({ ...x, left: left[x.key] || 0 }));
};

// The cards still to place, grouped by heading in syllabus order, filtered by
// heading chip and search (code or every typed word of name / label).
export const matchesCard = (card, comp, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const code = String(comp?.code || '').toLowerCase();
  if (code.includes(q)) return true;
  const hay = `${comp?.name || ''} ${comp?.ageGroup || ''} ${card?.label || ''} ${card?.heading || ''}`.toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
};

export const groupPool = (keys, cards) => {
  const groups = [];
  const byTitle = new Map();
  for (const k of keys || []) {
    const card = cards[k];
    if (!card) continue;
    const title = card.heading || 'Other';
    let g = byTitle.get(title);
    if (!g) {
      g = { title, keys: [] };
      byTitle.set(title, g);
      groups.push(g);
    }
    g.keys.push(k);
  }
  return groups;
};

export const headingChips = (order, pool, cards) => {
  const left = {};
  for (const k of pool || []) {
    const h = cards[k]?.heading || 'Other';
    left[h] = (left[h] || 0) + 1;
  }
  const seen = [];
  for (const k of order || []) {
    const h = cards[k]?.heading || 'Other';
    if (!seen.includes(h)) seen.push(h);
  }
  return seen.map((h) => ({ key: h, label: h, left: left[h] || 0 }));
};

// ---- timing -----------------------------------------------------------------

// One length for the board, the CSV and the save (code review R1-F4): whole
// minutes within what the backend stores - a round 0-600 (0 = nobody entered),
// a note 1-600.
export const itemMinutes = (item, cards, comps) => {
  if (item.type === 'note') return Math.min(NOTE_MAX, Math.max(1, Math.round(Number(item.minutes)) || 1));
  if (item.problem) return 0;
  const card = cardOf(item, cards);
  return card ? Math.min(ROUND_MAX, Math.max(0, Math.round(cardMinutes(card, entrantsOf(comps[item.competitionId]), item.atATime)))) : 0;
};

// A stage's times, one entry per item (scheduleTiming.flow). A flagged row
// takes no time and its fixed start is ignored, matching what a save keeps.
export const stageTimes = (stage, cards, comps) =>
  flow(
    stage.items.map((it) => (it.problem ? { ...it, fixed: false } : it)),
    stage.startMin,
    (it) => itemMinutes(it, cards, comps)
  );

// ---- save -------------------------------------------------------------------

// PUT /oireachtas/:id/schedule body. Flagged rows are left out. Every item
// carries its minutes (the estimate the organizer saw) and its dancers at a
// time, so the tablets and the public schedule read the same numbers.
export const savePayload = (revision, days, board, cards, comps) => ({
  revision,
  days: days.map((day) => ({
    day,
    stages: (board[day] || []).map((stage, i) => ({
      stage: i + 1,
      stageName: String(stage.name || '').trim().slice(0, 60) || `Stage ${i + 1}`,
      startTime: formatTime(Number.isFinite(stage.startMin) ? stage.startMin : DEFAULT_START),
      items: stage.items
        .filter((it) => !it.problem)
        .map((it) => {
          const fixedAt = it.fixed && Number.isFinite(it.fixedAt) ? formatTime(it.fixedAt) : null;
          const minutes = itemMinutes(it, cards, comps);
          if (it.type === 'note') {
            return { key: it.key, type: 'note', label: String(it.label || '').trim().slice(0, 60) || 'Note', minutes, fixedAt };
          }
          const card = cardOf(it, cards);
          const k = card?.kind === 'reel_continuous' ? 1 : Number(it.atATime) || Number(card?.defaultAtATime) || 2;
          return { key: it.key, type: 'round', competition_id: it.competitionId, round: it.round, atATime: Math.min(6, Math.max(1, k)), minutes, fixedAt };
        }),
    })),
  })),
});

// ---- CSV --------------------------------------------------------------------

const cell = (v) => {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; // never a spreadsheet formula
  return `"${s.replace(/"/g, '""')}"`;
};

export const CSV_HEADER = ['Day', 'Stage', 'Start', 'End', 'Minutes', 'Block', 'Code', 'Competition', 'Round', 'Dancers', 'At a time'];

export const csvRows = (days, board, cards, comps) => {
  const rows = [CSV_HEADER];
  for (const day of days) {
    for (const stage of board[day] || []) {
      const times = stageTimes(stage, cards, comps);
      const heads = headingsOf(stage.items, cards);
      let block = '';
      stage.items.forEach((it, j) => {
        if (it.problem) return;
        const t = times[j];
        const when = [formatTime(t.start), formatTime(t.end), Math.round(t.minutes)];
        if (it.type === 'note') {
          block = '';
          rows.push([dayLabel(day), stage.name, ...when, '', '', it.label || 'Note', '', '', '']);
          return;
        }
        if (heads[j]) block = heads[j];
        const card = cardOf(it, cards);
        const comp = comps[it.competitionId];
        const k = card?.kind === 'reel_continuous' ? 1 : Number(it.atATime) || card?.defaultAtATime || 2;
        rows.push([dayLabel(day), stage.name, ...when, block, comp?.code || it.code, comp?.name || '', card?.label || `Round ${it.round}`, entrantsOf(comp), k]);
      });
    }
  }
  return rows;
};

export const toCsv = (rows) => `${rows.map((r) => r.map(cell).join(',')).join('\r\n')}\r\n`;
